import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { business } from '../data/site';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeo } from '../hooks/useSeo';
import type { Localized } from '../types/content';
interface LegalProps {
  kind: 'privacy' | 'terms';
}
const copy: Record<LegalProps['kind'], {
  title: Localized;
  intro: Localized;
  sections: {
    heading: Localized;
    body: Localized;
  }[];
}> = {
  privacy: {
    title: {
      en: 'Privacy Policy',
      es: 'Política de Privacidad'
    },
    intro: {
      en: 'M&S Multiservices collects only the information needed to prepare your estimate and complete your project.',
      es: 'M&S Multiservices recopila únicamente la información necesaria para preparar tu presupuesto y completar tu proyecto.'
    },
    sections: [{
      heading: {
        en: 'Information we collect',
        es: 'Información que recopilamos'
      },
      body: {
        en: 'Name, phone number, email address, property address and the project details you choose to share through our estimate form, phone calls or WhatsApp messages.',
        es: 'Nombre, teléfono, correo electrónico, dirección de la propiedad y los detalles del proyecto que decidas compartir por el formulario, llamadas o mensajes de WhatsApp.'
      }
    }, {
      heading: {
        en: 'How we use it',
        es: 'Cómo la usamos'
      },
      body: {
        en: 'Only to contact you about your estimate, schedule work, and follow up after completion. We never sell or rent your information, and we do not send marketing messages you did not ask for.',
        es: 'Solo para contactarte sobre tu presupuesto, agendar el trabajo y dar seguimiento al terminar. Nunca vendemos ni rentamos tu información, y no enviamos mensajes publicitarios que no hayas solicitado.'
      }
    }, {
      heading: {
        en: 'Project photos',
        es: 'Fotos del proyecto'
      },
      body: {
        en: 'We photograph our work for quality records and our portfolio. Photos never include identifying information about you, and we will not publish images of your property if you ask us not to.',
        es: 'Fotografiamos nuestro trabajo para registros de calidad y nuestro portafolio. Las fotos nunca incluyen información que te identifique, y no publicaremos imágenes de tu propiedad si nos pides que no lo hagamos.'
      }
    }, {
      heading: {
        en: 'Your choices',
        es: 'Tus opciones'
      },
      body: {
        en: `You can request a copy or deletion of your information at any time by emailing ${business.email} or calling ${business.phoneDisplay}.`,
        es: `Puedes solicitar una copia o la eliminación de tu información en cualquier momento escribiendo a ${business.email} o llamando al ${business.phoneDisplay}.`
      }
    }]
  },
  terms: {
    title: {
      en: 'Terms of Service',
      es: 'Términos de Servicio'
    },
    intro: {
      en: 'These terms describe how estimates, scheduling, payment and our workmanship warranty work.',
      es: 'Estos términos describen cómo funcionan los presupuestos, la programación, los pagos y nuestra garantía de mano de obra.'
    },
    sections: [{
      heading: {
        en: 'Estimates',
        es: 'Presupuestos'
      },
      body: {
        en: 'Estimates are free and carry no obligation. Written quotes are valid for 30 days and are based on the conditions visible at the time of inspection. Hidden damage discovered during work is discussed with you and approved in writing before any additional charge.',
        es: 'Los presupuestos son gratis y sin compromiso. Las cotizaciones escritas son válidas por 30 días y se basan en las condiciones visibles al momento de la inspección. Los daños ocultos que se descubran durante el trabajo se discuten contigo y se aprueban por escrito antes de cualquier cargo adicional.'
      }
    }, {
      heading: {
        en: 'Scheduling and access',
        es: 'Programación y acceso'
      },
      body: {
        en: 'Start dates are confirmed in writing. You agree to provide safe access to the work area, water and power. Weather may shift exterior work; we notify you as early as possible.',
        es: 'Las fechas de inicio se confirman por escrito. Acuerdas proporcionar acceso seguro al área de trabajo, agua y electricidad. El clima puede mover el trabajo exterior; te avisamos lo antes posible.'
      }
    }, {
      heading: {
        en: 'Payment',
        es: 'Pagos'
      },
      body: {
        en: 'A deposit may be required for material purchases on larger projects. The balance is due upon completion and your final walkthrough approval.',
        es: 'Puede requerirse un depósito para la compra de materiales en proyectos grandes. El saldo se paga al terminar y después de tu aprobación en el recorrido final.'
      }
    }, {
      heading: {
        en: 'Workmanship warranty',
        es: 'Garantía de mano de obra'
      },
      body: {
        en: 'Our labor is guaranteed in writing on every quote. The warranty covers defects in our workmanship and does not cover normal wear, damage caused by others, or failures of materials supplied by the customer.',
        es: 'Nuestra mano de obra está garantizada por escrito en cada presupuesto. La garantía cubre defectos de nuestro trabajo y no cubre el desgaste normal, daños causados por terceros ni fallas de materiales proporcionados por el cliente.'
      }
    }, {
      heading: {
        en: 'Licensing and insurance',
        es: 'Licencia y seguro'
      },
      body: {
        en: 'M&S Multiservices is a licensed and insured contractor operating in the State of Texas. Documentation is available on request before work begins.',
        es: 'M&S Multiservices es un contratista con licencia y seguro que opera en el Estado de Texas. La documentación está disponible a solicitud antes de comenzar el trabajo.'
      }
    }]
  }
};
export function Legal({
  kind
}: LegalProps) {
  const {
    t,
    lang
  } = useLanguage();
  const content = copy[kind];
  useSeo({
    title: `${content.title[lang]} | M&S Multiservices`,
    description: content.intro[lang]
  });
  return <>
      <PageHeader eyebrow="M&S Multiservices" title={t(content.title)} description={t(content.intro)} showActions={false} data-id="element-498" data-mp="9dk17nay8oqs" />
      <section className="bg-white" data-id="element-499" data-mp="i4u4w7foq197">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20" data-id="element-500" data-mp="i5vmkur7hlzw">
          <div className="space-y-10" data-id="element-501" data-mp="n4otu5fwggrm">
            {content.sections.map(section => <div key={section.heading.en} data-id="element-502" data-mp="s302wdyden8s">
                <h2 className="font-display text-2xl text-navy" data-id="element-503" data-mp="s1oxk9pa17fk">{t(section.heading)}</h2>
                <p className="mt-3 leading-relaxed text-ink/75" data-id="element-504" data-mp="x3o41sgbckgq">{t(section.body)}</p>
              </div>)}
          </div>
          <p className="mt-12 border-t border-navy/10 pt-6 text-sm text-ink/60" data-id="element-505" data-mp="111u46hmnc1so">
            {t({
            en: 'Last updated September 2026. Questions about this page? Call us — we would rather explain it than have you guess.',
            es: 'Última actualización: septiembre de 2026. ¿Preguntas sobre esta página? Llámanos — preferimos explicarte que dejarte con dudas.'
          })}
          </p>
        </div>
      </section>
    </>;
}